/*
 * pp-landing-template — Lógica de landing pages de captura.
 *
 * Responsabilidades:
 *   1. Scroll-to-form: click en cualquier [data-pp-scroll-to-form]
 *      hace smooth-scroll al form principal (#pp-landing-form-top)
 *      y mete focus en el primer input vacío.
 *   2. Phone input: solo dígitos, max 10.
 *   3. Submit: validación + recolección de UTMs + POST al endpoint.
 *   4. Success state: muestra mensaje + WhatsApp deep link.
 *   5. Error state: mantiene form + muestra alert + WhatsApp escape.
 *
 * Tracking automático en cada submit:
 *   - utm_source, utm_medium, utm_campaign, utm_term, utm_content
 *   - gclid (Google Ads click ID)
 *   - referrer, page_url, page_title
 *   - landing_handle (page.handle desde Liquid)
 *   - landing_keyword (Google Ads keyword del metaobject)
 *   - submitted_at (ISO timestamp)
 *   - user_agent
 *
 * Eventos GA4 (no-op si window.gtag no existe):
 *   - landing_view        al cargar
 *   - landing_cta_click   click en cualquier CTA scroll-to-form
 *   - landing_form_submit submit exitoso (= conversión primaria)
 *   - landing_form_error  submit fallido (endpoint caído / validación server)
 */
(function () {
  'use strict';

  const root = document.querySelector('[data-pp-landing]');
  if (!root) return;

  // ───── GA4 helper ─────────────────────────────────────────
  const landingHandle = root.dataset.landingHandle || '';
  const landingKeyword = root.dataset.landingKeyword || '';

  function track(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, Object.assign({
      landing_handle: landingHandle,
      landing_keyword: landingKeyword
    }, params || {}));
  }

  track('landing_view');

  // ───── Active form picker — mobile vs desktop sidebar ───
  // El theme renderiza dos forms (sidebar sticky para desktop +
  // inline para mobile). Cada uno display:none en el otro breakpoint.
  // offsetParent === null indica display:none, así detecto el visible.
  function getActiveForm() {
    const sidebarForm = document.getElementById('pp-landing-form-top');
    const mobileForm = document.getElementById('pp-landing-form-mobile');

    if (sidebarForm && sidebarForm.offsetParent !== null) return sidebarForm;
    if (mobileForm && mobileForm.offsetParent !== null) return mobileForm;
    return sidebarForm || mobileForm;
  }

  // ───── Scroll-to-form ─────────────────────────────────────
  function scrollToForm() {
    const form = getActiveForm();
    if (!form) return;

    const offset = 80;
    const top = form.getBoundingClientRect().top + window.pageYOffset - offset;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top,
      behavior: reduced ? 'auto' : 'smooth'
    });

    setTimeout(() => {
      const inputs = form.querySelectorAll('input[required], textarea[required]');
      for (const input of inputs) {
        if (!input.value.trim()) {
          input.focus({ preventScroll: true });
          return;
        }
      }
      const submit = form.querySelector('[data-pp-lf-submit]');
      if (submit) submit.focus({ preventScroll: true });
    }, reduced ? 0 : 600);
  }

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-pp-scroll-to-form]');
    if (!trigger) return;
    e.preventDefault();

    // Pre-fill especial: si el trigger es un tipo card, override del
    // mensaje (hidden) del form con esa variante específica. Da contexto
    // rico al lead ("este buscaba pizza chica") en lugar del genérico.
    const tipoTitle = trigger.getAttribute('data-pp-tipo-title');
    if (tipoTitle) {
      const subject = landingKeyword
        ? `Cotización de ${landingKeyword} — ${tipoTitle}`
        : `Cotización: ${tipoTitle}`;
      document.querySelectorAll('[data-pp-landing-form] [name="mensaje"]').forEach((input) => {
        input.value = subject;
      });
    }

    track('landing_cta_click', {
      cta_tipo: tipoTitle || trigger.textContent.trim().slice(0, 60)
    });

    scrollToForm();
  });

  // ───── Teléfono: el celular de 10 dígitos, venga como venga ─────
  // Antes: maxlength 10 + cortar a 10 en cada tecla. Pegar «81 2188 6111» lo
  // dejaba en 8 dígitos y daba error; pegar «+52 81 2188 6111» mandaba
  // «5281218861», que PASABA la validación y le llegaba a ventas como un
  // número equivocado (12-oct-2026). Ahora se quita el formato y la lada del
  // país (52 / 521), y sólo entonces se cuenta.
  const telefonoMx = (v) => {
    let d = String(v == null ? '' : v).replace(/\D/g, '');
    if (d.length === 13 && d.startsWith('521')) d = d.slice(3);
    else if (d.length === 12 && d.startsWith('52')) d = d.slice(2);
    return d;
  };
  window.PPLandingTelefonoMx = telefonoMx;
  document.querySelectorAll('[data-pp-lf-phone]').forEach((input) => {
    // Mientras escribe o pega no se le corta nada: se acomoda al salir del campo.
    input.addEventListener('blur', () => {
      const d = telefonoMx(input.value);
      if (d) input.value = d;
    });
  });

  // ───── UTM + context capture ──────────────────────────────
  // Match V2 behavior: lee URL params primero, cae a cookies si no
  // existen. Cookies vienen de pp-utm-tracking.js (cargado en theme.liquid)
  // que las setea con 7-day expiration cada vez que el user llega con
  // gclid o utm_* en la URL. Eso permite persistencia entre páginas y
  // submits posteriores.
  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(^|; )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : '';
  }

  function captureTrackingContext(form) {
    const params = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'adgroup', 'matchtype'];
    const utms = {};
    utmKeys.forEach((k) => {
      const fromUrl = params.get(k);
      const fromCookie = readCookie(k);
      const value = fromUrl || fromCookie;
      if (value) utms[k] = value;
    });

    return {
      ...utms,
      landing_handle: form.dataset.landingHandle || '',
      landing_keyword: form.dataset.landingKeyword || '',
      referrer: document.referrer || '',
      page_url: window.location.href,
      page_title: document.title,
      user_agent: navigator.userAgent,
      submitted_at: new Date().toISOString()
    };
  }

  // ───── Form submit handler ────────────────────────────────
  document.querySelectorAll('[data-pp-landing-form]').forEach((form) => {
    const submitBtn = form.querySelector('[data-pp-lf-submit]');
    const submitLabel = submitBtn ? submitBtn.querySelector('.pp-lf__submit-label') : null;
    if (submitLabel) {
      submitBtn.dataset.originalLabel = submitLabel.textContent;
    }
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await handleSubmit(form);
    });
  });

  async function handleSubmit(form) {
    const submitBtn = form.querySelector('[data-pp-lf-submit]');
    const errorBox = form.querySelector('[data-pp-lf-error]');
    const submitLabel = submitBtn.querySelector('.pp-lf__submit-label');
    const submitIcon = submitBtn.querySelector('.pp-lf__submit-icon');
    const submitSpinner = submitBtn.querySelector('.pp-lf__submit-spinner');

    // Reset error state
    errorBox.hidden = true;
    while (errorBox.firstChild) errorBox.removeChild(errorBox.firstChild);
    form.querySelectorAll('.pp-lf__input--invalid').forEach((el) => {
      el.classList.remove('pp-lf__input--invalid');
    });

    const nombre = form.querySelector('[name="nombre"]').value.trim();
    const whatsapp = telefonoMx(form.querySelector('[name="whatsapp"]').value);
    const correo = form.querySelector('[name="correo"]').value.trim();
    const mensaje = form.querySelector('[name="mensaje"]').value.trim();
    const comentarioEl = form.querySelector('[name="comentario"]');
    const comentario = comentarioEl ? comentarioEl.value.trim() : '';

    const errors = [];
    if (nombre.length < 2) {
      errors.push({ field: 'nombre', msg: 'Necesitamos tu nombre.' });
    }
    if (!/^\d{10}$/.test(whatsapp)) {
      errors.push({ field: 'whatsapp', msg: 'Revisa tu WhatsApp: son 10 dígitos (lada + número), por ejemplo 81 2188 6111.' });
    }
    // RFC 5322 simplified — buena suficiente para validación cliente
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(correo)) {
      errors.push({ field: 'correo', msg: 'El correo no parece válido.' });
    }

    if (errors.length > 0) {
      errors.forEach((err) => {
        const input = form.querySelector(`[name="${err.field}"]`);
        if (input) input.classList.add('pp-lf__input--invalid');
      });
      errorBox.textContent = errors.map((e) => e.msg).join(' ');
      errorBox.hidden = false;
      const firstInvalid = form.querySelector('.pp-lf__input--invalid');
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    // Loading
    submitBtn.disabled = true;
    submitLabel.textContent = 'Enviando…';
    submitIcon.hidden = true;
    submitSpinner.hidden = false;

    const tracking = captureTrackingContext(form);
    const payload = {
      lead: {
        nombre,
        whatsapp_e164: '+52' + whatsapp,
        whatsapp_raw: whatsapp,
        correo,
        mensaje,
        comentario: comentario || null
      },
      source: 'landing_page',
      tracking
    };

    const endpoint = form.dataset.leadEndpoint;
    const whatsappFallback = form.dataset.whatsappFallback;

    try {
      if (!endpoint) throw new Error('Lead endpoint not configured');

      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) throw new Error(`Endpoint returned ${resp.status}`);

      showSuccess(form, whatsappFallback, payload);

      track('landing_form_submit', {
        form_id: form.dataset.formId || '',
        gclid_present: payload.tracking.gclid ? 'yes' : 'no'
      });

      window.dispatchEvent(new CustomEvent('pp:landing_lead', {
        detail: { form_id: form.dataset.formId, payload }
      }));

    } catch (err) {
      console.error('[pp-landing-form] Submit failed:', err);
      track('landing_form_error', {
        form_id: form.dataset.formId || '',
        error_message: String(err && err.message || err).slice(0, 100)
      });
      renderErrorWithWhatsAppEscape(errorBox, whatsappFallback, payload);
      submitBtn.disabled = false;
      submitLabel.textContent = submitBtn.dataset.originalLabel || 'Cotizar Ahora';
      submitIcon.hidden = false;
      submitSpinner.hidden = true;
    }
  }

  function renderErrorWithWhatsAppEscape(errorBox, whatsappFallback, payload) {
    errorBox.hidden = false;
    while (errorBox.firstChild) errorBox.removeChild(errorBox.firstChild);

    errorBox.appendChild(document.createTextNode('No pudimos enviar tu solicitud. '));

    const link = document.createElement('a');
    link.href = buildWhatsAppDeepLink(whatsappFallback, payload);
    link.target = '_blank';
    link.rel = 'noopener';
    link.style.fontWeight = '700';
    link.style.textDecoration = 'underline';
    link.textContent = 'Escríbenos por WhatsApp';
    errorBox.appendChild(link);

    errorBox.appendChild(document.createTextNode(' mientras lo resolvemos.'));
  }

  function showSuccess(form, whatsappFallback, payload) {
    const successBox = form.querySelector('[data-pp-lf-success]');
    const whatsappLink = form.querySelector('[data-pp-lf-whatsapp-link]');

    if (whatsappLink) {
      whatsappLink.href = buildWhatsAppDeepLink(whatsappFallback, payload);
    }

    form.classList.add('pp-lf--submitted');
    successBox.hidden = false;
  }

  function buildWhatsAppDeepLink(number, payload) {
    if (!number) return '#';
    const cleanNumber = number.replace(/\D/g, '');
    const text = encodeURIComponent(
      `Hola, soy ${payload.lead.nombre}. ${payload.lead.mensaje} `
      + `(Origen: ${payload.tracking.landing_handle || 'landing'})`
    );
    return `https://wa.me/${cleanNumber}?text=${text}`;
  }

  // ────────────────────────────────────────────────────────
  // Mobile sticky CTA + modal handlers (Opción 2 UX).
  // El sticky-cta abre el modal; close button + ESC + backdrop lo
  // cierran. Body scroll lock con clase pp-landing-modal-open.
  // ────────────────────────────────────────────────────────
  const landingModal = document.querySelector('[data-pp-landing-modal]');
  if (landingModal) {
    function openLandingModal() {
      landingModal.classList.add('is-open');
      landingModal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('pp-landing-modal-open');
    }
    function closeLandingModal() {
      landingModal.classList.remove('is-open');
      landingModal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('pp-landing-modal-open');
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-pp-landing-modal-open]')) {
        e.preventDefault();
        openLandingModal();
        return;
      }
      if (e.target.closest('[data-pp-landing-modal-close]')) {
        e.preventDefault();
        closeLandingModal();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && landingModal.classList.contains('is-open')) {
        closeLandingModal();
      }
    });
  }

  // ────────────────────────────────────────────────────────
  // Sticky CTA vs banner de cookies.
  // El banner (.pp-cookie-banner, z-index 9000) se dibuja encima del
  // sticky CTA y le roba el tap: elementFromPoint sobre el centro del
  // CTA devolvía el botón del banner. Aquí publicamos el alto real del
  // banner en --pp-cookie-h para que el CSS suba el sticky mientras
  // esté visible, y lo regrese a su lugar al cerrarse.
  // Solo corre si el sticky trae data-pp-over-cookies.
  // ────────────────────────────────────────────────────────
  // ────────────────────────────────────────────────────────
  // Carruseles con asomo (catálogo y testimonios): puntos + contador.
  // El scroll lo hace el navegador con scroll-snap; esto solo refleja en
  // qué tarjeta vas. Sin carruseles en el DOM, no corre nada.
  // ────────────────────────────────────────────────────────
  document.querySelectorAll('[data-pp-carrusel-track]').forEach((track) => {
    const bloque = track.closest('section') || track.parentElement;
    const dots = bloque.querySelector('[data-pp-carrusel-dots]');
    const count = bloque.querySelector('[data-pp-carrusel-count]');
    const items = track.children;
    const total = items.length;
    if (!total) return;

    if (dots) {
      for (let i = 0; i < total; i += 1) {
        const punto = document.createElement('span');
        punto.className = 'pp-landing__carrusel-dot' + (i === 0 ? ' is-on' : '');
        dots.appendChild(punto);
      }
    }

    const sincronizar = () => {
      const x = track.scrollLeft;
      let actual = 0;
      let menor = Infinity;
      for (let i = 0; i < total; i += 1) {
        const d = Math.abs(items[i].offsetLeft - track.offsetLeft - x);
        if (d < menor) { menor = d; actual = i; }
      }
      if (dots) {
        for (let j = 0; j < dots.children.length; j += 1) {
          dots.children[j].classList.toggle('is-on', j === actual);
        }
      }
      if (count) count.textContent = (actual + 1) + ' / ' + total;
    };

    let raf = null;
    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = null;
        sincronizar();
      });
    }, { passive: true });
  });

  const stickyOverCookies = document.querySelector('.pp-landing__sticky-cta[data-pp-over-cookies]');
  if (stickyOverCookies) {
    const banner = document.querySelector('.pp-cookie-banner');
    if (banner) {
      const syncCookieHeight = () => {
        const visible = !banner.hidden && banner.classList.contains('is-visible');
        const alto = visible ? Math.ceil(banner.getBoundingClientRect().height) : 0;
        document.documentElement.style.setProperty('--pp-cookie-h', alto + 'px');
      };

      syncCookieHeight();
      new MutationObserver(syncCookieHeight).observe(banner, {
        attributes: true,
        attributeFilter: ['class', 'hidden']
      });
      if ('ResizeObserver' in window) {
        new ResizeObserver(syncCookieHeight).observe(banner);
      }
      window.addEventListener('resize', syncCookieHeight);
    }
  }
})();
